import * as React from "react";

const DEFAULT_OPTIONS: MutationObserverInit = { childList: true };

/**
 * Observe DOM mutations on `target` for the lifetime of the component,
 * disconnecting the observer when the target changes or the component unmounts.
 *
 * A `null`/`undefined` target observes nothing — there is deliberately no
 * fallback target, so a ref that has not resolved yet (or a node that only
 * exists after some async work) is safe to pass without a caller-side guard.
 *
 * `options` defaults to `{ childList: true }`: `MutationObserver.observe()`
 * throws unless at least one of `childList`, `attributes` or `characterData` is
 * set, and `childList` is what every current call site wants. Options that the
 * native API rejects still throw, as they do with a raw observer.
 */
export function useMutationObserver(
  target: Node | null | undefined,
  callback: MutationCallback,
  options: MutationObserverInit = DEFAULT_OPTIONS,
) {
  const savedCallback = React.useRef(callback);

  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Destructure so an inline options object doesn't re-observe on every render.
  const { attributes, attributeOldValue, attributeFilter, characterData, characterDataOldValue, childList, subtree } =
    options;

  // attributeFilter is the one MutationObserverInit field that is an array, so
  // it can't go into a dependency array by identity. Attribute names cannot
  // contain commas, so a joined string is a lossless key for its contents and
  // the array is rebuilt from that key inside the effect. An empty filter joins
  // to "", which splits back to [""], so empty names are dropped to preserve the
  // native meaning of `attributeFilter: []` (match no attribute).
  const attributeFilterKey = attributeFilter?.join(",");

  React.useEffect(() => {
    if (!target) {
      return;
    }

    const observer = new MutationObserver((mutations, instance) => {
      savedCallback.current(mutations, instance);
    });

    observer.observe(target, {
      attributes,
      attributeOldValue,
      attributeFilter: attributeFilterKey?.split(",").filter((name) => name !== ""),
      characterData,
      characterDataOldValue,
      childList,
      subtree,
    });

    return () => {
      observer.disconnect();
    };
  }, [
    target,
    attributes,
    attributeOldValue,
    attributeFilterKey,
    characterData,
    characterDataOldValue,
    childList,
    subtree,
  ]);
}
